import { useCallback, useEffect, useRef, useState } from 'react'
import { api, setReauthPrompt } from '../api'
import { Icon } from './Icon'
import { useDialog } from '../useDialog'

/**
 * "Confirm it is you" — the dialog behind every sensitive change.
 *
 * Changing who reads the site or who runs the workspace is refused by the
 * server when this session has not proven its holder recently. This dialog is
 * how it proves them: it appears when any request comes back with
 * `code: 'reauth_required'`, asks for the password, and on success the API
 * layer replays the refused request exactly as it was — so whatever the person
 * had typed into the screen underneath (the domain, the passphrase) is still
 * there and still goes through.
 *
 * Mounted once, registered with the API layer, and owns nothing but the
 * password field. It reuses the shortcut sheet's scrim/sheet shell rather than
 * inventing a modal of its own.
 *
 * An account that signs in with Google has no password to confirm. The API
 * refreshes the completed federated sign-in proof automatically, so this
 * dialog never presents a password field that cannot work.
 */
interface Ask {
  passwordless: boolean
  resolve: (confirmed: boolean) => void
}

export function ConfirmIdentity() {
  const [ask, setAsk] = useState<Ask | null>(null)
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [codeRequired, setCodeRequired] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const field = useRef<HTMLInputElement>(null)
  /*
   * Escape cancels, which is what the backdrop and the Cancel button do. A
   * password prompt somebody cannot dismiss with the keyboard is a trap, and
   * cancelling is already a supported answer — the act it was guarding simply
   * does not happen.
   */
  const panel = useDialog<HTMLDivElement>(ask !== null, () => finish(false))

  useEffect(() => {
    setReauthPrompt(
      (info) =>
        new Promise<boolean>((resolve) => {
          setAsk({ ...info, resolve })
          setPassword('')
          setCode('')
          setCodeRequired(false)
          setError(null)
        }),
    )
    return () => setReauthPrompt(null)
  }, [])

  const finish = useCallback(
    (confirmed: boolean) => {
      ask?.resolve(confirmed)
      setAsk(null)
      setPassword('')
      setError(null)
    },
    [ask],
  )

  useEffect(() => {
    if (!ask) return
    if (!ask.passwordless) {
      field.current?.focus()
      return
    }
    if (codeRequired) {
      field.current?.focus()
      return
    }
    setBusy(true)
    void api
      .reauth('')
      .then((result) => {
        if (result.twoFactor) {
          setCodeRequired(true)
          return
        }
        finish(true)
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Could not confirm this session.')
      })
      .finally(() => setBusy(false))
  }, [ask, codeRequired, finish])

  /**
   * Escape cancels this dialog and ONLY this dialog. Captured, because the
   * drawers underneath listen for Escape on the window too, and one keypress
   * must not close the question and the screen that asked it together.
   */
  useEffect(() => {
    if (!ask) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      finish(false)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [ask, finish])

  const confirm = async () => {
    if (busy || (ask?.passwordless ? !code : !password)) return
    setBusy(true)
    setError(null)
    try {
      await api.reauth(password, ask?.passwordless ? code : undefined)
      finish(true)
    } catch (e) {
      // The server's sentence: a wrong password and a rate limit each say what
      // to do next, and inventing a vaguer one here would lose that.
      setError(e instanceof Error ? e.message : 'Could not confirm that.')
    } finally {
      setBusy(false)
    }
  }

  if (!ask) return null

  return (
    <div className="scrim" onMouseDown={() => finish(false)}>
      <div
        className="sheet confirm-identity"
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="Confirm it is you"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="sheet__head">
          <h2 className="sheet__title">
            <Icon name="lock" size={17} />
            Confirm it is you
          </h2>
          <button className="btn btn--ghost btn--icon" onClick={() => finish(false)}>
            <Icon name="close" size={15} label="Close" />
          </button>
        </header>
        <div className="sheet__body">
          {ask.passwordless ? (
            <>
              {codeRequired ? (
                <form
                  className="confirm-identity__form"
                  onSubmit={(e) => {
                    e.preventDefault()
                    void confirm()
                  }}
                >
                  <p className="confirm-identity__text">
                    Enter the six-digit code from your authenticator.
                  </p>
                  <input
                    ref={field}
                    className="input"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    disabled={busy}
                    aria-label="Authenticator code"
                  />
                  <div className="confirm-identity__actions">
                    <button className="btn btn--primary" type="submit" disabled={!code || busy}>
                      Confirm
                    </button>
                    <button className="btn btn--ghost" type="button" onClick={() => finish(false)}>
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <p className="confirm-identity__text">Confirming your Google sign-in…</p>
              )}
              {error && (
                <p className="confirm-identity__error" role="alert">
                  {error}
                </p>
              )}
              {!codeRequired && (
                <div className="confirm-identity__actions">
                  <button className="btn btn--ghost" onClick={() => finish(false)} disabled={busy}>
                    Cancel
                  </button>
                </div>
              )}
            </>
          ) : (
            <form
              className="confirm-identity__form"
              onSubmit={(e) => {
                e.preventDefault()
                void confirm()
              }}
            >
              <p className="confirm-identity__text">
                This change affects who can read this site or who can act in this workspace. Confirm
                your password to continue — what you were doing is kept and goes through right
                after.
              </p>
              <input
                ref={field}
                className="input"
                type="password"
                autoComplete="current-password"
                placeholder="Your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {error && (
                <p className="confirm-identity__error" role="alert">
                  {error}
                </p>
              )}
              <div className="confirm-identity__actions">
                <button className="btn btn--ghost" type="button" onClick={() => finish(false)}>
                  Cancel
                </button>
                <button className="btn btn--primary" type="submit" disabled={busy || !password}>
                  {busy ? <Icon name="spinner" size={13} /> : null}
                  Confirm
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
